<template>
  <div class="game-selection" :style="{ backgroundImage: `url(${backgroundImage})` }" @click="handleBackgroundClick">
    <!-- Back Button -->
    <a class="back-button" @click="goBack">⬅ {{back_label[language]}}</a>

    <!-- Game Images -->
    <img v-for="(game, index) in gamePositions" :key="index" :src="game.image" :style="game.style"
      @click.stop="handleGameClick(game)" :class="{ 'glow-effect': selectedGame && selectedGame.name === game.name }"
      alt="game-icon" class="game-image" />

    <!-- Game Name Display -->
    <div v-if="selectedGame" class="game-name-display">
      {{ gameNames[selectedGame.name][language] }}
    </div>

    <transition name="fade">
      <div v-if="showMotivation">
        <div class="motivation-container">
          <div class="motivation-box">
            <h2 class="motivation-text">{{ motivationMessage }}</h2>
          </div>
        </div>
        <div class="professor-pep-container">
          <img :src="profPepImage" alt="Professor Pep" class="professor-pep" />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { sounds } from '@/utils/sounds.js';

export default {
  props: ['time', 'background', 'games'],
  data() {
    let widthRatio = 700; // Aspect ratio (width:height), for example, 2:1
    let heightRatio = 850; // Aspect ratio (width:height), for example, 2:1

    let sz = this.time.toLowerCase() === 'afternoon'
      ? Math.round(window.innerHeight * 0.32)
      : Math.round(window.innerHeight * 0.44);

    // Calculate width and height based on aspect ratio
    let width = Math.round(sz * widthRatio / (widthRatio + heightRatio));
    let height = Math.round(sz * heightRatio / (widthRatio + heightRatio));

    return {
      back_label : {en:"Back", el:"Πίσω", it:"Indietro", ro:"Înapoi", uk:"Назад"},
      
      gameNames: {
      'Stretching': { en: 'Stretching', el: 'Διατάσεις', it: 'Stretching', ro: 'Întindere', uk: 'Розтяжка' },
      
      'Find the Odd': { en: 'Find the Odd', el: 'Βρες το λάθος', it: 'Scegli il diverso', ro: 'Alege diferitul', uk: 'Вибери зайве' },
      'Select Meal': { en: 'Select Meal', el: 'Αντιστοίχιση στο γεύμα', it: 'Seleziona pasto', ro: 'Selectează masa', uk: 'Вибери страву' },
      'Food Matching': { en: 'Food Matching', el: 'Ταξινόμηση τροφίμων', it: 'Abbinamento cibi', ro: 'Potrivire alimente', uk: 'Співставлення їжі' },
      'Food Pyramid': { en: 'Food Pyramid', el: 'Διατροφική πυραμίδα', it: 'Piramide alimentare', ro: 'Piramida alimentară', uk: 'Харчова піраміда' },

      'Hands Game': { en: 'Hands Game', el: 'Πιάσε τα αστέρια', it: 'Gioco delle mani', ro: 'Joc de mâini', uk: 'Гра з руками' },
      'Frames': { en: 'Frames', el: 'Μπες στο καρέ', it: 'Cornici', ro: 'Cadre', uk: 'Кадри' },
      'Jumps Precision': { en: 'Jumps Precision', el: 'Άλματα με ακρίβεια', it: 'Precisione nei salti', ro: 'Precizie în sărituri', uk: 'Точність стрибків' },
      'Running': { en: 'Running', el: 'Τρέξιμο', it: 'Corsa', ro: 'Alergare', uk: 'Біг' },
      'Body Move': { en: 'Body Move', el: 'Κίνηση σώματος', it: 'Movimento del corpo', ro: 'Mișcare corp', uk: 'Рух тіла' },
      'Jump Rope': { en: 'Jump Rope', el: 'Σχοινάκι', it: 'Corda per saltare', ro: 'Coardă', uk: 'Скакалка' },
      'Jumps': { en: 'Jumps', el: 'Άλματα', it: 'Salti', ro: 'Sărituri', uk: 'Стрибки' },

      'High Knees': { en: 'High Knees', el: 'Γόνατα ψηλά', it: 'Ginocchia alte', ro: 'Genunchi sus', uk: 'Високі коліна' },
      'Situps': { en: 'Situps', el: 'Κοιλιακοί', it: 'Addominali', ro: 'Abdomene', uk: 'Прес' },
      'Pushups': { en: 'Pushups', el: 'Κάμψεις', it: 'Flessioni', ro: 'Flotări', uk: 'Віджимання' },
      'Jumping Jacks': { en: 'Jumping Jacks', el: 'Άλματα Jumping Jacks', it: 'Saltelli Jumping Jacks', ro: 'Sărituri Jumping Jacks', uk: 'Стрибки Jumping Jacks' },
      'Plank': { en: 'Plank', el: 'Σανίδα', it: 'Plank', ro: 'Plank', uk: 'Планка' },
      },

      backgroundImage: this.background,
      gameList: Array.isArray(this.games) ? this.games : JSON.parse(this.games),
      gamePositions: [],
      imageSize: { width, height }, // Store width and height separately
      selectedGame: null,

      showMotivation: true,
      motivationMessages: {
        morning: {
          en: [
            "Good morning! Let's collect the blue seeds!",
            "Rise and shine! The blue seeds await!",
            "Morning energy! Find those blue seeds!"
          ],
          el: [
            "Καλημέρα! Ας μαζέψουμε τους μπλε σπόρους!",
            "Ξύπνα! Οι μπλε σπόροι σε περιμένουν!",
            "Πρωινή ενέργεια! Βρες τους μπλε σπόρους!"
          ],
          it: [
            "Buongiorno! Raccogliamo i semi blu oggi!",
            "Svegliati! I semi blu ti aspettano!",
            "Energia mattutina! Trova i semi blu!"
          ],
          ro: [
            "Bună dimineața! Să adunăm astăzi semințele albastre!",
            "Trezește-te! Semințele albastre te așteaptă!",
            "Energie de dimineață! Găsește semințele albastre!"
          ],
          uk: [
            "Доброго ранку! Зберемо сьогодні синє насіння!",
            "Прокидайся! Синє насіння чекає на тебе!",
            "Ранкова енергія! Знайди синє насіння!"
          ]
        },
        noon: {
          en: [
            "It's noon—time to gather the red seeds!",
            "Midday mission: collect the red seeds!",
            "Red seeds await at noon!"
          ],
          el: [
            "Είναι μεσημέρι—ώρα να μαζέψουμε τους κόκκινους σπόρους!",
            "Μεσημεριανή αποστολή: μαζέψτε τους κόκκινους σπόρους!",
            "Οι κόκκινοι σπόροι μας περιμένουν το μεσημέρι!"
          ],
          it: [
            "È mezzogiorno—tempo di raccogliere i semi rossi!",
            "Missione di mezzogiorno: raccogli i semi rossi!",
            "I semi rossi ti aspettano a mezzogiorno!"
          ],
          ro: [
            "Este prânz—este timpul să adunăm semințele roșii!",
            "Misiune de prânz: adună semințele roșii!",
            "Semințele roșii te așteaptă la prânz!"
          ],
          uk: [
            "Полудень — час збирати червоне насіння!",
            "Полуденна місія: збирай червоне насіння!",
            "Червоне насіння чекає опівдні!"
          ]
        },
        afternoon: {
          en: [
            "This afternoon we're after the green seeds!",
            "Afternoon alert: find the green seeds!",
            "Green seeds are the treasure this afternoon!"
          ],
          el: [
            "Αυτό το απόγευμα μαζεύουμε τους πράσινους σπόρους!",
            "Βρες τους πράσινους σπόρους!",
            "Οι πράσινοι σπόροι μας περιμένουν!"
          ],
          it: [
            "Questo pomeriggio puntiamo ai semi verdi!",
            "Avviso pomeridiano: trova i semi verdi!",
            "I semi verdi sono il tesoro di questo pomeriggio!"
          ],
          ro: [
            "În această după-amiază adunăm semințele verzi!",
            "Alertă de după-amiază: găsește semințele verzi!",
            "Semințele verzi sunt comoara după-amiezii!"
          ],
          uk: [
            "Сьогодні після обіду збираємо зелене насіння!",
            "Попередження після обіду: знайди зелене насіння!",
            "Зелене насіння — скарб цього післяобіддя!"
          ]
        },
        night: {
          en: [
            "Evening is here, find the yellow seeds!",
            "Nighttime mission: gather the yellow seeds!",
            "Yellow seeds glow bright at night!"
          ],
          el: [
            "Ήρθε το βράδυ, βρείτε τους κίτρινους σπόρους!",
            "Νυχτερινή αποστολή: μαζέψτε τους κίτρινους σπόρους!",
            "Οι κίτρινοι σπόροι λάμπουν τη νύχτα!"
          ],
          it: [
            "È sera, trova i semi gialli!",
            "Missione notturna: raccogli i semi gialli!",
            "I semi gialli brillano di notte!"
          ],
          ro: [
            "A sosit seara, găsește semințele galbene!",
            "Misiune de noapte: adună semințele galbene!",
            "Semințele galbene strălucesc noaptea!"
          ],
          uk: [
            "Вечір настав, знайди жовте насіння!",
            "Нічна місія: збирай жовте насіння!",
            "Жовте насіння яскраво світиться вночі!"
          ]
        }
      },
      motivationMessage: "",
      profPepImage: require('@/assets/game-characters/professor_pep_talking.gif'),
      profPepTalking: require('@/assets/game-characters/professor_pep_talking.gif'),
      profPepStill: require('@/assets/game-characters/prof-pep.png'),
    };

  },
    created()
  {
    this.language = localStorage.getItem("language") || "en"; // Default to English
    console.log("LANGUAGE "+this.language);
  },
  mounted() {
    sounds.ambient3.play();
    this.generateGamePositions();
    const msgs = (this.motivationMessages[this.time.toLowerCase()] || {})[this.language] || [];
    this.motivationMessage = msgs.length ? msgs[Math.floor(Math.random() * msgs.length)] : "";
    this.profPepImage = this.profPepTalking;
    setTimeout(() => {
      this.showMotivation = false;
      this.profPepImage = this.profPepStill;
    }, 5000);
  },
  beforeUnmount() {
    sounds.ambient3.stop();
  },
  methods: {
        async goBack() {
      sounds.whoosh.play();
      this.$router.push({ name: 'MainScene' });
    },
    handleGameClick(game) {
      if (this.selectedGame && this.selectedGame.name === game.name) {
        // Play shine sound when starting the game
        sounds.score_sound.play();
        // Start the game if clicked again
        this.startGame(game.name);
      } else {
        // Select the game if clicked for the first time
        this.selectedGame = game;
      }
    },
    handleBackgroundClick() {
      // Reset selection when clicking outside game icons
      this.selectedGame = null;
    },
    startGame(game) {
      const gameRoutes = {
        'Situps': 'SitupsGame',
        'Running': 'RunningGame',
        'Pushups': 'PushupsGame',
        'Jumping Jacks': 'JumpingJacksGame',
        'Jumps': 'JumpsGame',
        'High Knees': 'HighKneesGame',
        'Hands Game': 'HandsGame',
        'Frames': 'FramesGame',
        'Body Move': 'BodyMoveGame',
        'Jump Rope': 'JumpRopeGame',
        'Pick the Odd': 'PickTheOddGame',
        'Select Meal': 'SelectMealGame',
        'Find the Odd': 'PickTheOddGame',
        'Food Pyramid': 'FoodPyramidGame',
        'Food Matching': 'FoodMatchingGame',
        'Build Your Week': 'BuildWeekGame',
        'Stretching': 'StretchingGame',
        'Plank': 'PlankGame',
        'Jumps Precision': 'JumpsPrecisionGame',
      };

      const routeName = gameRoutes[game];

      if (routeName) {
        this.$router.push({
          name: routeName,
          params: {
            suntime: this.time,
            background: this.background,
            games: this.games,
          },
        });
      } else {
        console.error(`No route found for game: ${game}`);
      }
    },
    generateGamePositions() {
      const gameImages = {
        'Select Meal': require('@/assets/game-icons/icon_select_meal.jpg'),
        'Find the Odd': require('@/assets/game-icons/icon_pick_odd.jpg'),
        'Build Your Week': require('@/assets/game-icons/icon_build_week.jpg'),
        'Food Matching': require('@/assets/game-icons/icon_nutrients_matching.jpg'),
        'Food Pyramid': require('@/assets/game-icons/icon_pyramid.jpg'),
        'Jump Rope': require('@/assets/game-icons/icon_jump_rope.jpg'),
        'Body Move': require('@/assets/game-icons/icon_center_body_movement2.jpg'),
        'Frames': require('@/assets/game-icons/icon_frames.jpg'),
        'Hands Game': require('@/assets/game-icons/icon_hands.jpg'),
        'High Knees': require('@/assets/game-icons/icon_high_knees.jpg'),
        'Jumping Jacks': require('@/assets/game-icons/icon_jumping_jacks.jpg'),
        'Jumps': require('@/assets/game-icons/icon_jumping2.jpg'),
        'Pushups': require('@/assets/game-icons/icon_pushups.jpg'),
        'Running': require('@/assets/game-icons/icon_running2.jpg'),
        'Situps': require('@/assets/game-icons/icon_situps2.jpg'),
        'Stretching': require('@/assets/game-icons/icon_stretching.jpg'),
        'Plank': require('@/assets/game-icons/icon_plank.jpg'),
        'Jumps Precision': require('@/assets/game-icons/icon_jumping.jpg'),
      };

      const widthMultiplier = window.innerWidth / 1024;
      const heightMultiplier = window.innerHeight / 1792;

      const currentDay = new Date().getDay();
      // const special = [1, 3, 6].includes(currentDay) // Monday, Wednesday, Saturday
      const special = [].includes(currentDay) 

      // Define positions based on time
      const timeBasedPositions = {
        morning: [
          { x: '58', y: '715' },
          { x: '577', y: '890' },
          { x: '490', y: '430' },
        ],
        noon: [
          { x: '531', y: '504' },
          { x: '531', y: '982' },
          { x: '74', y: '504' },
          { x: '74', y: '982' },
        ],
        afternoon: [
          [
            { x: '30', y: '664' },
            { x: '340', y: '759' },
            { x: '32', y: '1369' },
            { x: '7', y: '1012' },
            { x: '200', y: '321' },
            { x: '693', y: '1003' },
            { x: '673', y: '666' },
            { x: '668', y: '1364' },
            { x: '356', y: '1228' },
          ],
          [
            { x: '355', y: '835' },
            { x: '48', y: '1100' },
            { x: '402', y: '482' },
            { x: '92', y: '1443' },
            { x: '11', y: '761' },
            { x: '95', y: '423' },
            { x: '290', y: '83' },
            { x: '713', y: '1003' },
            { x: '723', y: '666' },
            { x: '703', y: '1364' },
            { x: '396', y: '1228' },
          ],

        ],
        night: [
          { x: '58', y: '715' },
          { x: '577', y: '890' },
          { x: '490', y: '430' },
        ],
      };

      let positions = timeBasedPositions[this.time.toLowerCase()];

      if (this.time.toLowerCase() == "afternoon") {
        if (special) {
          positions = positions[0];
        }
        else {
          positions = positions[1];
        }
      }
      console.log("positions: ", positions);

      this.gamePositions = this.gameList.map((game, index) => {
        const position = positions[index % positions.length];

        const scaledX = Math.round(parseInt(position.x) * widthMultiplier);
        const scaledY = Math.round(parseInt(position.y) * heightMultiplier);

        return {
          name: game,
          image: gameImages[game] || require('@/assets/game-icons/icon_pick_odd.jpg'),
          style: {
            position: 'absolute',
            top: `${scaledY}px`,
            left: `${scaledX}px`,
            width: `${this.imageSize.width}px`,  // Use width here
            height: `${this.imageSize.height}px`, // Use height here
            cursor: 'pointer',
            transition: 'transform 0.3s ease',
            objectFit: 'contain',
          },
        };
      });
    },
  },
};
</script>

<style>
.glow-effect {
  box-shadow: 0 0 10px #ffecb3, 0 0 20px #ffe0b2, 0 0 30px #ffffff, 0 0 40px #ffffff;
  transition: box-shadow 0.3s ease-in-out;
}

.game-name-display {
  position: fixed;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 18px;
  z-index: 10;
  text-align: center;
}

.game-selection {
  width: 100vw;
  height: 100vh;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.back-button {
  position: absolute;
  top: 20px;
  left: 20px;
  background-color: rgba(0, 0, 0, 0.5);
  color: #fff;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  font-size: 14px;
  cursor: pointer;
  z-index: 10;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.7);
}

.game-image {
  object-fit: contain;
  transition: transform 0.3s ease;
  position: absolute;
}

.game-image:hover {
  transform: scale(1.2);
}

@media (max-width: 768px) {
  .game-image {
    width: 100px;
    height: 100px;
  }
}

.motivation-container {
  position: fixed;
  bottom: 15%;
  left: 40%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  gap: 10px;
  z-index: 1003;
}

.motivation-box {
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 8px;
  padding: 8px 12px;
  max-width: 60vw;
  white-space: normal;
  word-wrap: break-word;
  text-align: center;
}

.professor-pep {
  height: 30vh !important;
  width: auto !important;
  max-height: none !important;
  max-width: none !important;
  display: block;
}

.motivation-text {
  font-size: 2vh;
  font-weight: bold;
  text-align: center;
  -webkit-text-stroke: 0.5px black;
  z-index: 1003;
}

.professor-pep-container {
  position: fixed;
  bottom: 0%;
  left: 85%;
  transform: translateX(-50%);
  z-index: 1003;
}
</style>
